// Suggested prompts for the platform assistant (plan 2026-08-02).
//
// Single source of truth for two surfaces: the drawer's empty state and the
// dashboard chat bar's chip row. They used to be separate hardcoded lists, and
// the customer docs article repeats them a third time
// (docs/getting-started/using-the-assistant.mdx, "Ask it something") — keep all
// three in step when editing.
//
// Static and curated on purpose: not backend-driven, not LLM-generated. These
// teach a first-time user what the assistant is FOR, so they should read as
// capabilities rather than as clever questions.

export type SuggestionAction =
  /** Send the label to the assistant as a question. */
  | "ask"
  /** Hand off to the insights chat instead — the assistant has no SQL tools. */
  | "insights";

export interface Suggestion {
  label: string;
  action: SuggestionAction;
}

/**
 * The assistant has no SQL tools at all, so a data question reaches insights
 * via a `start_data_analysis` proposal the user has to confirm. This chip keeps
 * the one-click route to data analysis alive and discoverable.
 */
const ANALYSE: Suggestion = { label: "Analyse my data", action: "insights" };

/** Default set — the account has at least one source connected. */
export const SUGGESTIONS: Suggestion[] = [
  { label: "Is my data up to date?", action: "ask" },
  { label: "How do I connect a data source?", action: "ask" },
  { label: "Create a report", action: "ask" },
  ANALYSE,
];

/**
 * Shown only when BOTH source fetches succeeded and returned nothing. Never
 * key this off an empty array alone: a failed fetch also leaves the arrays
 * empty, and telling a tenant with ten sources to connect one is worse than
 * showing the generic set.
 */
export const SUGGESTIONS_NO_SOURCES: Suggestion[] = [
  { label: "How do I connect a data source?", action: "ask" },
  { label: "What can you do for me?", action: "ask" },
  ANALYSE,
];

/**
 * Prompts for the drawer's empty state. Every entry MUST be answerable by the
 * assistant itself — the drawer sends what it is given straight to the model,
 * so a navigate-style label ("Analyse my data") would be asked as a question.
 *
 * Deliberately its own list rather than a filter over SUGGESTIONS: the dashboard
 * has room for three chips of which one is a hand-off, while the drawer's empty
 * state has three slots for real capabilities. Filtering would silently leave it
 * with two and drop the segment example, which is the one that shows a reader
 * the assistant can *do* things rather than just answer.
 */
export const DRAWER_EXAMPLE_PROMPTS: string[] = [
  "Is my data up to date?",
  "Create a segment of visitors who viewed the pricing page",
  "How do I connect a data source?",
];
